import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Video } from "lucide-react";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import CommissionDialog from "./CommissionDialog";

// Floating "Record" button — always visible for authenticated users so they
// can start filming from anywhere on the site. Routes to the Dashboard with
// a query param that auto-opens the recorder dialog.
const FloatingRecorderFAB = () => {
  const { user } = useAuth();

  // X-funnel attribution flush: if the user arrived via /x/welcome the
  // source slug waits in localStorage; once they're signed in, tie it to
  // their account for the Growth Agent's conversion reporting.
  useEffect(() => {
    if (!user) return;
    let src;
    try { src = localStorage.getItem("ge_x_src"); } catch { return; }
    if (!src) return;
    api.post("/growth/x-attribution", { src })
      .then(() => { try { localStorage.removeItem("ge_x_src"); } catch { /* noop */ } })
      .catch(() => { /* retry next mount */ });
  }, [user]);
  const navigate = useNavigate();
  const location = useLocation();
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [unreadCommissions, setUnreadCommissions] = useState(0);
  // Track whether the persistent player overlay is mounted in the DOM.
  // The overlay is rendered by <PlayerLayout/>'s PersistentOverlay,
  // which lives INSIDE the PlayerProvider context — but this FAB lives
  // OUTSIDE that provider (it's in <AppShell/>, a sibling of the
  // routes). A React-context check therefore returns null here, so we
  // use a tiny MutationObserver on document.body to react to the
  // overlay element appearing / disappearing. This stays accurate
  // across route changes without restructuring the provider tree.
  const [overlayOpen, setOverlayOpen] = useState(false);
  useEffect(() => {
    const SELECTOR = '[data-testid="persistent-overlay-wrap"]';
    const check = () => setOverlayOpen(!!document.querySelector(SELECTOR));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // Poll commission notifications (visibility-aware)
  useEffect(() => {
    if (!user) { setUnreadCommissions(0); return; }
    let cancelled = false;
    const fetchNotifs = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await api.get(`/notifications/commissions`);
        if (!cancelled) setUnreadCommissions(res.data?.unread || 0);
      } catch (err) { /* silent */ }
    };
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60000);
    const onVisible = () => { if (!document.hidden) fetchNotifs(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [user]);

  // For logged-out viewers, both buttons still render — clicking them
  // fires a global 'open-auth' event that Header catches and pops the
  // signup/login modal. This way casual visitors see what the site does
  // ("you can record! you can commission!") instead of an empty screen.
  // Hide on the Dashboard itself — the existing buttons live there.
  // Also hide on Pricing — the FAB overlaps the tier cards on mobile,
  // and users on /pricing are choosing a plan, not recording videos.
  if (location.pathname === '/dashboard') return null;
  if (location.pathname === '/pricing') return null;

  // Jun 11, 2026 — Eric's standing rule: Record + Commission buttons
  // are ALWAYS available, including on the dynamic player overlay. The
  // previous "hide when overlay open" branch was wrong; the FAB
  // cluster's right-edge gutter (`mr-20 sm:mr-0` on overlay content,
  // added Jun 9) already keeps it from obscuring titles/comments.
  // Removed.

  const openAuth = () => {
    window.dispatchEvent(new CustomEvent('open-auth'));
  };
  const handleRecord = () => {
    if (!user) { openAuth(); return; }
    // Leaving video fullscreen (if active) so the recorder dialog is
    // never buried under the fullscreen overlay.
    window.dispatchEvent(new CustomEvent('ge-exit-video-fs'));
    navigate('/dashboard?record=1');
  };
  const handleCommission = () => {
    if (!user) { openAuth(); return; }
    setShowCommissionDialog(true);
  };

  return (
    <>
      {/* Stack of floating action buttons — bottom right */}
      <div className="fixed bottom-20 right-5 z-40 flex flex-col items-end gap-2" data-testid="floating-fab-stack">
        <button
          onClick={handleCommission}
          title="Commission a video — invite a creator to film for you"
          data-testid="global-commission-fab"
          className="relative flex items-center gap-2 px-3 py-2 rounded-full bg-amber-500 hover:bg-amber-400 text-gray-900 shadow-lg shadow-amber-500/40 border border-amber-300/40 transition-all hover:scale-105 text-xs font-semibold"
        >
          <Sparkles className="h-4 w-4" />
          <span className="hidden sm:inline">Commission</span>
          {unreadCommissions > 0 && (
            <span
              data-testid="commission-unread-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-black"
            >
              {unreadCommissions > 99 ? '99' : unreadCommissions}
            </span>
          )}
        </button>
        <button
          onClick={handleRecord}
          title="Start filming — record a new video"
          data-testid="global-record-fab"
          className="flex items-center gap-2 px-4 py-3 rounded-full bg-rose-600 hover:bg-rose-500 text-white shadow-lg shadow-rose-500/40 border border-rose-400/40 transition-all hover:scale-105"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>
          <Video className="h-5 w-5" />
          <span className="hidden sm:inline text-sm font-semibold tracking-wide">Record</span>
        </button>
      </div>
      {showCommissionDialog && (
        <CommissionDialog open={showCommissionDialog} onClose={() => setShowCommissionDialog(false)} />
      )}
    </>
  );
};

export default FloatingRecorderFAB;
